/**
 * UPT-2124 J4 (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2-10) — le SOCLE COMMUN des outils d'interface : l'environnement (traduction, navigation, journal), les
 * réponses (introuvable + résumé de l'écran, ambiguïté, geste annulé), le verrou « un seul geste à la fois », le clic joué après le fantôme, le journal des
 * intentions (outil, niveau, NOM de l'élément, page — jamais la valeur saisie). `uiTools` (lire, naviguer, cliquer, défiler) et `uiWriteTools` (remplir,
 * choisir, annuler, confirmer) s'appuient dessus.
 */
import type { VoiceRegisteredTool, VoiceToolOutput } from '../voiceToolRegistry';
import type { VoiceIntentBody } from '../voiceTypes';
import { clickElement, contextOf, isDisabled, type UiCandidate } from './uiActor';
import { playUiGhost } from './uiGhostStore';
import { readScreen } from './uiReader';
import { normalizeUiText, type UiRisk } from './uiRiskTable';

/** Une entrée du menu de navigation au runtime (déjà traduite). */
export interface UiNavEntry {
  label: string;
  /** Le chemin concret (jetons `:workspaceId` / `:year` résolus). */
  path: string;
  /** Le chemin dans le menu (« Finance › Budgets »), pour départager. */
  trail: string;
}

export interface UiToolsEnv {
  document: Document;
  /** Traduction d'une clé `voice.ui.*` (l'acteur ne connaît pas react-intl). */
  t: (id: string, values?: Record<string, string | number>) => string;
  navigate: (to: string | number) => void;
  navEntries: () => UiNavEntry[];
  pathname: () => string;
  /** Durée du fantôme (tests : 0). */
  ghostMs?: number;
  /** Journal des intentions (`postVoiceIntent`) ; absent : rien n'est journalisé. */
  journal?: (body: VoiceIntentBody) => void;
  /** Attente (liste ARIA qui s'ouvre) ; tests : instantanée. */
  sleep?: (ms: number) => Promise<void>;
}

/** Ce que les outils d'écriture (J4b) apportent au clic : le formulaire modifié par Géode, et la confirmation d'un clic qui écrit. */
export interface UiWriteTools {
  tools: VoiceRegisteredTool[];
  /** Le bouton est dans un formulaire (ou un dialogue) que Géode a modifié. */
  isTouched: (element: HTMLElement) => boolean;
  /** Pose la carte de confirmation d'un clic confirmé (J2d) ; rend le texte de la question, sans rien cliquer. */
  askToConfirmClick: (candidate: UiCandidate, touchedForm: boolean) => VoiceToolOutput;
}

/** Le chemin sans identifiants (le journal et la pile d'annulation ne gardent que la forme de la page). */
export const pageKey = (pathname: string): string =>
  pathname
    .split('/')
    .map((segment) => (/^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(segment) ? ':id' : segment))
    .join('/');

export const json = (body: Record<string, unknown>): VoiceToolOutput => ({ outputJson: JSON.stringify(body) });

export const clip = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

export const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface UiToolKit {
  env: UiToolsEnv;
  doc: Document;
  t: UiToolsEnv['t'];
  journal: (tool: string, outcome: VoiceIntentBody['outcome'], level: UiRisk, name?: string) => void;
  /** « Je ne vois pas … à l'écran » suivi du résumé de l'écran. */
  notFound: (name: string) => VoiceToolOutput;
  /** Plusieurs candidats : aucune action, la liste est dite. */
  ambiguous: (name: string, candidates: UiCandidate[]) => VoiceToolOutput;
  /** Un seul geste à la fois. */
  exclusive: (run: () => Promise<VoiceToolOutput>) => Promise<VoiceToolOutput>;
  /** Le fantôme a été annulé (bouton, `Échap`) : rien n'a été joué. */
  cancelledOutput: (tool: string, level: UiRisk, name: string) => VoiceToolOutput;
  /** Fantôme puis clic natif ; le journal garde l'outil, le niveau et le nom. */
  performClick: (tool: string, candidate: UiCandidate, level: UiRisk) => Promise<VoiceToolOutput>;
}

export const createUiToolKit = (env: UiToolsEnv): UiToolKit => {
  const { document: doc, t } = env;
  let busy = false;

  const journal: UiToolKit['journal'] = (tool, outcome, level, name) => {
    env.journal?.({ channel: 'voice', op: tool, outcome, level, field: name ? clip(name, 80) : undefined, page: pageKey(env.pathname()) });
  };

  const notFound: UiToolKit['notFound'] = (name) => {
    const result = readScreen(doc);
    const summary = result.ok === true ? result.text : '';
    return json({ error: 'not_found', answer: `${t('voice.ui.answer.notFound', { name })}${summary ? `\n${summary}` : ''}` });
  };

  const ambiguous: UiToolKit['ambiguous'] = (name, candidates) => {
    // Des noms distincts (« Open 1 », « Open 2 ») se disent tels quels ; des noms identiques (« Ouvrir » ×2) se départagent par leur ligne ou leur carte.
    const distinct = new Set(candidates.map((candidate) => normalizeUiText(candidate.name))).size === candidates.length;
    const choices = candidates
      .slice(0, 5)
      .map((candidate) => (distinct ? candidate.name : contextOf(candidate.element, candidate.name) || candidate.name))
      .join(` ${t('voice.ui.or')} `);
    return json({
      error: 'ambiguous',
      answer: t('voice.ui.answer.ambiguous', { name, choices }),
      candidates: candidates.slice(0, 5).map((candidate) => ({ name: candidate.name, context: contextOf(candidate.element, candidate.name) })),
    });
  };

  const exclusive: UiToolKit['exclusive'] = async (run) => {
    if (busy) {
      return json({ error: 'busy', answer: t('voice.ui.answer.busy') });
    }
    busy = true;
    try {
      return await run();
    } finally {
      busy = false;
    }
  };

  const cancelledOutput: UiToolKit['cancelledOutput'] = (tool, level, name) => {
    journal(tool, 'rejected', level, name);
    return json({ error: 'cancelled', cancelled: true, answer: t('voice.ui.answer.cancelled') });
  };

  const performClick: UiToolKit['performClick'] = async (tool, candidate, level) => {
    const { element, name } = candidate;
    if (!element.isConnected || isDisabled(element)) {
      return json({ error: 'disabled', answer: t('voice.ui.answer.disabled', { name }) });
    }
    const outcome = await playUiGhost({ label: t('voice.ui.ghost.click', { name }), element, ms: env.ghostMs });
    if (outcome === 'cancelled') return cancelledOutput(tool, level, name);
    clickElement(element);
    journal(tool, 'applied', level, name);
    return json({ done: true, level, answer: t('voice.ui.answer.clicked', { name }) });
  };

  return { env, doc, t, journal, notFound, ambiguous, exclusive, cancelledOutput, performClick };
};
